/**
 * DKM-5143 (C yaklaşımı): açılışta "kaydedilmemiş değişiklik" sorusunu önleyen karşılaştırma tabanı yardımcısı.
 *
 * Sorun: kayıt açılınca kontroller (dk-ctl-gen-kontrol vb.) kayıtlı kodu yükleyip dataResult ile bildirir; bu olay
 * kullanıcının listeden seçim yapmasıyla aynıdır. Ekranın işleyicisi kayıttan türetilen alanları (ad, açıklama,
 * ilçe, birim...) forma yazar. Ekranlar karşılaştırma kopyasını bu asenkron yüklemeden önce aldığı için DB'de boş ya
 * da farklı olan türetilmiş alan Kapat/İptal'de farka dönüşür ve soru çıkar.
 *
 * Çözüm: gelen kayıt formdaki kodla aynıysa (yankı = açılıştaki yükleme) işleyici çalıştırılır ve işleyicinin BU
 * çağrıda değiştirdiği alanlar karşılaştırma tabanına da yazılır. Zamana bağlı değildir (yükleme geç gelse de aynı
 * çalışır). Kullanıcının başka alanlarda yaptığı değişiklikler tabana karışmaz; kullanıcı farklı bir kod seçtiyse
 * (yankı değil) taban değişmez ve soru çıkar.
 *
 * Opt-in: hiçbir mevcut bileşeni değiştirmez; ekran, türetilmiş alan yazan işleyicisinin başına bir satır ekler:
 * ```ts
 * setX(e: any) {
 *   if (kontrolYuklemesiniTabanaYaz(this.form, this.formCopy, kontrolKoduAyni(e?.Kod, this.form?.XKod), () => this.setX(e))) return;
 *   ...mevcut gövde...
 * }
 * ```
 * Pilot: DKM-5122 madde 7 (sas-abone-bilgileri-detay, aynı mantığın bileşen içi hâli).
 */

/** İşleyici çalışırken aynı form için iç içe çağrıyı (işleyicinin kendini yeniden çağırması) normal akışa bırakır */
const tabanaYaziliyor = new WeakSet<object>();

/**
 * @param form Ekranın düzenlenen form verisi
 * @param taban Karşılaştırma kopyası (Kapat/İptal'de form ile JSON karşılaştırılan nesne)
 * @param yanki Gelen kayıt formdaki kayıtlı kodla aynı mı (bkz. kontrolKoduAyni)
 * @param isleyici Türetilmiş alanları forma yazan işleyici gövdesi
 * @returns true ise işleyici burada çalıştırıldı, çağıran hemen dönmeli; false ise çağıran normal akışa devam eder
 *          (yankı değil, taban henüz alınmamış ya da zaten bu yardımcının içinden çağrıldı).
 */
export function kontrolYuklemesiniTabanaYaz(form: object | null | undefined, taban: object | null | undefined,
                                            yanki: boolean, isleyici: () => void): boolean {
  if (!yanki || !form || !taban || tabanaYaziliyor.has(form)) {
    return false;
  }
  tabanaYaziliyor.add(form);
  try {
    const f = form as Record<string, unknown>;
    const t = taban as Record<string, unknown>;
    const once: Record<string, string | undefined> = {};
    for (const alan of Object.keys(f)) {
      once[alan] = JSON.stringify(f[alan]);
    }
    isleyici();
    for (const alan of new Set([...Object.keys(once), ...Object.keys(f)])) {
      const simdi = JSON.stringify(f[alan]);
      if (simdi !== once[alan]) {
        t[alan] = simdi === undefined ? undefined : JSON.parse(simdi);
      }
    }
  } finally {
    tabanaYaziliyor.delete(form);
  }
  return true;
}

/** Kontrolden gelen kod formdaki kodla aynı mı (sayı/metin farkı gözetilmez; boş kod yankı sayılmaz) */
export function kontrolKoduAyni(gelen: unknown, kayitli: unknown): boolean {
  return gelen != null && kayitli != null && gelen !== '' && String(gelen) === String(kayitli);
}
